import { Col, Container, Row } from 'reactstrap';
import { useJointId } from '../../../lib/util';
import { GET_JOINT_DETAILS } from '../../../graph-queries/tar/joint';
import { useQuery } from '@apollo/client';
import { TCertificate, TJoint, TJointInstallationStep } from '../../../lib/types';
import classes from './JointDetails.module.scss';
import { StepCard } from './JointSigningStep';
import FormModal from '../../molecules/FormModal/FormModal';
import JointSigningForm from './JointSigningForm';
import { Card, Tabs } from '@bphxd/ds-core-react';
import { useEffect, useMemo, useState } from 'react';
import AppLayout from '../../templates/AppLayout';
import {
  CertificateStatusEnum,
  CertificateTypeEnum,
} from '../../../graph-queries/__generated__/graphql';
import { GET_CERTIFICATE_DETAILS } from '../../../graph-queries/tar/certificate';

export type JointReadiness = {
  readyForLeakTest: boolean;
  readyForStartup: boolean;
};

export const getSigningStepParts = (supervisedAssembly: boolean, useSbt: boolean) => {
  const installationSteps = useSbt //if SBT there is not a joint tightened step. Witness is conditioned on input.
    ? supervisedAssembly
      ? [1, 2, 4]
      : [1, 2]
    : supervisedAssembly
      ? [1, 2, 3, 4]
      : [1, 2, 3];

  const leakServiceTestSteps = [5];
  const operationalSteps = [6, 7, 8, 9, 10, 11];

  return { installationSteps, leakServiceTestSteps, operationalSteps };
};

/**
 * Determines the readiness status of all joints in a certificate for leak test and startup phases.
 * 
 * @param certificate - The certificate containing joints to check
 * @param signingStepParts - Object containing required installation, leak test, and operational step numbers to be signed
 * @returns Object with readyForLeakTest and readyForStartup boolean flags
 */
export const jointStatus = (certificate: TCertificate): JointReadiness => {

  const certificateJoints = certificate?.tarJoints || [];

    if (certificateJoints.length === 0) {
      return { readyForLeakTest: false, readyForStartup: false };
    }
    //We will look at whether each joint is Supervised Assembly in the every block
    const stepParts = getSigningStepParts(true, certificate?.type === CertificateTypeEnum.Sbt);

    // --- Check readiness for Leak Test ---
    const allJointsReadyForLeakTest = certificateJoints.every((joint) => {
      const steps = joint.tarJointInstallationSteps || [];

      const requiredStepsForThisJoint = stepParts.installationSteps.filter(
        (stepNo) => !(stepNo === 4 && joint.supervisedAssembly === false)
      );

      return requiredStepsForThisJoint.every((requiredStepNo) => {
        const step = steps.find((s) => s.stepNo === requiredStepNo);
        return step?.signedBy;
      });
    });

    // --- Check readiness for Startup ---
    const allRequiredStepsForStartup = [
      ...stepParts.installationSteps,
      ...stepParts.leakServiceTestSteps,
    ];

    const allJointsReadyForStartup = certificateJoints.every((joint) => {
      const steps = joint.tarJointInstallationSteps || [];

      // Apply supervised assembly rule PER JOINT
      const requiredStepsForThisJoint = allRequiredStepsForStartup.filter(
        (stepNo) => !(stepNo === 4 && joint.supervisedAssembly === false)
      );

      return requiredStepsForThisJoint.every((requiredStepNo) => {
        const step = steps.find((s) => s.stepNo === requiredStepNo);
        return step?.signedBy;
      });
    });

    return {
      readyForLeakTest: allJointsReadyForLeakTest,
      readyForStartup: allJointsReadyForStartup,
    };
};

const JointDetailsPage = () => {
  const [part, setPart] = useState<
    'installationSteps' | 'leakServiceTestSteps' | 'operationalSteps'
  >('installationSteps');
  const jointId = useJointId();

  const {
    data,
    loading,
    refetch: refetchJointDetails,
  } = useQuery(GET_JOINT_DETAILS, {
    variables: { jointId },
    fetchPolicy: 'cache-and-network',
  });

  const jointDetails = data?.jointDetails as unknown as TJoint;
  const certificateId = jointDetails?.certificateId ?? -1;

  const {
    data: certificateDetails,
    loading: certificateLoading,
    refetch: refetchCertificateDetails,
  } = useQuery(GET_CERTIFICATE_DETAILS, {
    variables: { certificateId },
    skip: certificateId === -1,
    fetchPolicy: 'cache-and-network',
  });

  const certificate = certificateDetails?.certificateDetails as unknown as TCertificate;
  const certificateStatus = certificate?.status as CertificateStatusEnum;

  // Memoize signing step parts calculation based on current joint (supervisedAssembly and type)
  const signingStepParts = useMemo(() => {
    return getSigningStepParts(
      jointDetails?.supervisedAssembly,
      jointDetails?.tarCertificate?.type === CertificateTypeEnum.Sbt
    );
  }, [jointDetails?.supervisedAssembly, jointDetails?.tarCertificate?.type]);

  // Determine readiness across *all joints* in the certificate
  const { readyForLeakTest, readyForStartup } = useMemo(() => {
    return jointStatus(certificate);
  }, [certificate?.tarJoints]);

  // Helper function to check if all steps in a phase are signed
  const areAllStepsInPhaseSigned = (phaseSteps: number[]) => {
    if (!jointDetails?.tarJointInstallationSteps) return false;

    return phaseSteps.every((stepNo) => {
      const step = jointDetails.tarJointInstallationSteps?.find((s) => s.stepNo === stepNo);
      return step?.signedBy;
    });
  };

  // determine and set the appropriate tab based on phase completion
  const selectAppropriateTab = () => {
    if (!jointDetails?.tarJointInstallationSteps) return;

    const installationComplete = areAllStepsInPhaseSigned(signingStepParts.installationSteps);
    const leakTestComplete = areAllStepsInPhaseSigned(signingStepParts.leakServiceTestSteps);
    const operationalComplete = areAllStepsInPhaseSigned(signingStepParts.operationalSteps);

    // Select tab based on which phase has unsigned steps & user has permission to access the tab
    if (!installationComplete) {
      setPart('installationSteps');
    } else if (!leakTestComplete) {
      const phase = readyForLeakTest ? 'leakServiceTestSteps' : 'installationSteps';
      setPart(phase);
    } else if (!operationalComplete) {
      const phase = readyForStartup ? 'operationalSteps' : 'leakServiceTestSteps';
      setPart(phase);
    } else {
      // All steps signed - show operational
      setPart('operationalSteps');
    }
  };

  useEffect(() => {
    selectAppropriateTab();
  }, [jointDetails?.tarJointInstallationSteps, readyForLeakTest, readyForStartup]); // Re-run when joint details load or readiness changes

  const handleStepSigned = async () => {
    try {
      // refetch both joint and certificate details to get updated step information
      await Promise.all([refetchJointDetails(), refetchCertificateDetails()]);
    } catch (error) {
      console.error('Error refetching data after step signed:', error);
    }
  };

  return (
    <AppLayout>
      <Container>
        {loading || !jointDetails ? (
          'Loading...'
        ) : (
          <div className={classes.root}>
            <section id="section1" className="border border-primary bg-white">
              <header className="px-4">
                <div className={classes.title}>Joint Information</div>
              </header>
              <div className="p-4">
                <Row className="g-12">
                  <Col sm="3">
                    <div className={classes.field}>
                      <div className={classes.label}>Joint Number</div>
                      <div className={classes.value}>{jointDetails?.jointNumber}</div>
                    </div>
                  </Col>
                  <Col sm="2">
                    <div className={classes.field}>
                      <div className={classes.label}>Tag ID</div>
                      <div className={classes.value}>{jointDetails?.tagId}</div>
                    </div>
                  </Col>
                  <Col sm="2">
                    <div className={classes.field}>
                      <div className={classes.label}>P&ID No</div>
                      <div className={classes.value}>{jointDetails?.pIdNo}</div>
                    </div>
                  </Col>
                  <Col sm="5">
                    <div className={classes.field}>
                      <div className={classes.label}>Additional Details</div>
                      <div className={classes.value}>{jointDetails?.additionalDetails}</div>
                    </div>
                  </Col>
                </Row>
              </div>
            </section>

            {jointDetails?.tarCertificate?.type !== CertificateTypeEnum.Sbt && (
              /* Row to hold Joint Specification and Hydralic Tools side by side */
              <Row className="align-items-stretch mt-4 g-4">
                <Col sm="8">
                  <section id="section2" className="border border-primary bg-white h-100">
                    <header className="px-4">
                      <div className={classes.title}>Joint Specification</div>
                    </header>
                    <div className="p-4">
                      <Row className="g-6 mb-6">
                        <Col>
                          <div className={classes.field}>
                            <div className={classes.label}>Flange Size</div>
                            <div className={classes.value}>{jointDetails.flangeSize}</div>
                          </div>
                        </Col>
                        <Col>
                          <div className={classes.field}>
                            <div className={classes.label}>Rating</div>
                            <div className={classes.value}>{jointDetails.rating}</div>
                          </div>
                        </Col>
                        <Col>
                          <div className={classes.field}>
                            <div className={classes.label}>Bolt ⌀</div>
                            <div className={classes.value}>{jointDetails.boltDiameter}</div>
                          </div>
                        </Col>
                        <Col>
                          <div className={classes.field}>
                            <div className={classes.label}>Bolt Qty</div>
                            <div className={classes.value}>{jointDetails.boltQuantity}</div>
                          </div>
                        </Col>
                      </Row>
                      <Row className="g-6">
                        <Col>
                          <div className={classes.field}>
                            <div className={classes.label}>Bolt Length</div>
                            <div className={classes.value}>{jointDetails.boltLength}</div>
                          </div>
                        </Col>
                        <Col>
                          <div className={classes.field}>
                            <div className={classes.label}>Bolt Material</div>
                            <div className={classes.value}>{jointDetails.boltMaterial}</div>
                          </div>
                        </Col>
                        <Col>
                          <div className={classes.field}>
                            <div className={classes.label}>Gasket Type</div>
                            <div className={classes.value}>{jointDetails.gasketType}</div>
                          </div>
                        </Col>
                        <Col>
                          <div className={classes.field}>
                            <div className={classes.label}>Torque Value</div>
                            <div className={classes.value}>{jointDetails.torqueValue}</div>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </section>
                </Col>

                <Col sm="4">
                  <section id="section3" className="border border-primary bg-white h-100">
                    <header className="px-4">
                      <div className={classes.title}>Hydraulic Tools Used</div>
                    </header>
                    <div className="p-4">
                      <Row className="g-12 mb-6">
                        <Col>
                          <div className={classes.field}>
                            <div className={classes.label}>Tool Reference</div>
                            <div className={classes.value}>{jointDetails.hydraulicToolRef}</div>
                          </div>
                        </Col>
                        <Col>
                          <div className={classes.field}>
                            <div className={classes.label}>Pass</div>
                            <div className={classes.value}>{jointDetails.hydraulicToolPass}</div>
                          </div>
                        </Col>
                      </Row>
                      <Row className="g-12">
                        <Col>
                          <div className={classes.field}>
                            <div className={classes.label}>Pump Pressure (PSI)</div>
                            <div className={classes.value}>
                              {jointDetails.hydraulicToolPumpPressure}
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </section>
                </Col>
              </Row>
            )}

            <Row className="my-2 g-4">
              <Col sm="12">
                <Tabs tabStyle="standard" className={classes.tabs}>
                  <Tabs.Item
                    active={part === 'installationSteps'}
                    onClick={() => setPart('installationSteps')}
                  >
                    Installation
                  </Tabs.Item>
                  <Tabs.Item
                    active={part === 'leakServiceTestSteps'}
                    onClick={() => {
                      readyForLeakTest && setPart('leakServiceTestSteps');
                    }}
                    title={
                      readyForLeakTest ? 'Go to Leak/Service Test' : 'Not all joints are ready'
                    }
                    style={{ cursor: readyForLeakTest ? 'pointer' : 'default' }}
                  >
                    Leak/Service Test
                  </Tabs.Item>
                  <Tabs.Item
                    active={part === 'operationalSteps'}
                    onClick={() => {
                      readyForStartup && setPart('operationalSteps');
                    }}
                    title={readyForStartup ? 'Go to Operational Phase' : 'Not all joints are ready'}
                    className={readyForStartup ? 'disabled' : ''}
                    style={{ cursor: readyForStartup ? 'pointer' : 'default' }}
                  >
                    Operational Phase
                  </Tabs.Item>
                </Tabs>
              </Col>
              {jointDetails.tarJointInstallationSteps?.map((jis) =>
                signingStepParts[part].includes(jis.stepNo) ? (
                  <Col key={jis.stepId} sm="3">
                    <FormModal
                      title={
                        jointDetails.tarCertificate?.type === CertificateTypeEnum.Sbt &&
                          jis.stepNo === 2
                          ? 'Assembled'
                          : jis.stepTitle
                      }
                      renderForm={(onClose) => (
                        <JointSigningForm
                          joint={jointDetails}
                          step={jis}
                          onClose={onClose}
                          certificateStatus={certificateStatus}
                          onStepSigned={handleStepSigned}
                        />
                      )}
                      renderButton={(onClick) => (
                        <Card
                          onClick={
                            certificateDetails?.certificateDetails.status ===
                              CertificateStatusEnum.Approved
                              ? onClick
                              : undefined
                          }
                        >
                          <StepCard
                            step={signingStepParts[part].indexOf(jis.stepNo) + 1}
                            title={
                              jointDetails.tarCertificate?.type === CertificateTypeEnum.Sbt &&
                                jis.stepNo === 2
                                ? 'Assembled'
                                : jis.stepTitle
                            }
                            date={jis.signedAt}
                            signedBy={jis.signedBy}
                            comments={jis.comments}
                          />
                        </Card>
                      )}
                    />
                  </Col>
                ) : null
              )}
            </Row>
          </div>
        )}
      </Container>
    </AppLayout>
  );
};

JointDetailsPage.classes = classes;

export default JointDetailsPage;
